import { Injectable, signal } from '@angular/core';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  canInstall = signal(false);
  installed = signal(false);
  instructionsOpen = signal(false);
  promptDismissed = signal(false);
  transitionTutorialOpen = signal(false);
  isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  isAndroid = /android/i.test(navigator.userAgent);
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private readonly dismissKey = 'nzp_install_prompt_dismissed';

  constructor() {
    this.promptDismissed.set(localStorage.getItem(this.dismissKey) === '1');
    this.refreshInstalledState();
    window.addEventListener('beforeinstallprompt', event => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.canInstall.set(true);
    });
    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.canInstall.set(false);
      this.installed.set(true);
      this.instructionsOpen.set(false);
    });
    window.matchMedia('(display-mode: standalone)').addEventListener?.('change', () => this.refreshInstalledState());
  }

  async install() {
    if (this.deferredPrompt) {
      await this.deferredPrompt.prompt();
      const choice = await this.deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') this.canInstall.set(false);
      this.deferredPrompt = null;
      return;
    }
    this.instructionsOpen.set(true);
  }

  closeInstructions() { this.instructionsOpen.set(false); }

  dismissFloatingPrompt() {
    localStorage.setItem(this.dismissKey, '1');
    this.promptDismissed.set(true);
    this.transitionTutorialOpen.set(true);
  }

  closeTransitionTutorial() {
    this.transitionTutorialOpen.set(false);
  }

  private refreshInstalledState() {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    this.installed.set(standalone);
  }
}
